import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bell, AlertTriangle, AlertCircle, CheckCircle2, ClipboardList } from "lucide-react";
import { api, type NotificationItem } from "../lib/api.js";
import type { RouteTarget } from "../lib/useRoute.js";
import { timeAgo } from "../lib/timestamps.js";

const LAST_SEEN_KEY = "alternabe:notif-last-seen";

function icon(kind: NotificationItem["kind"]) {
  switch (kind) {
    case "error": return <AlertCircle className="h-4 w-4 text-red-500" />;
    case "warning": return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case "activity": return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    default: return <ClipboardList className="h-4 w-4 text-brand" />;
  }
}

export function NotificationsBell({ onNavigate }: { onNavigate: (r: RouteTarget) => void }) {
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState<string>(() => localStorage.getItem(LAST_SEEN_KEY) ?? "");
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications(),
    refetchInterval: 60_000,
  });

  const items = data?.items ?? [];
  const unread = items.filter((i) => i.ts && (!lastSeen || i.ts > lastSeen)).length;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const openMenu = () => {
    setOpen((o) => !o);
    if (data?.latestTs) {
      localStorage.setItem(LAST_SEEN_KEY, data.latestTs);
      setLastSeen(data.latestTs);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button className="btn-icon relative" title="Notifications" onClick={openMenu}>
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-xl border border-line bg-white shadow-xl">
          <div className="border-b border-line px-4 py-2.5 text-sm font-semibold text-ink">
            Notifications
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted">You’re all caught up.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    onNavigate(n.route as RouteTarget);
                    setOpen(false);
                  }}
                  className="flex w-full items-start gap-3 border-b border-line/60 px-4 py-3 text-left hover:bg-line/30"
                >
                  <span className="mt-0.5 shrink-0">{icon(n.kind)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{n.title}</span>
                    <span className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                      {n.subtitle && <span className="truncate">{n.subtitle}</span>}
                      {n.ts && <span className="shrink-0">· {timeAgo(n.ts)}</span>}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
